import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const radioGroupItemVariants = cva(
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        /** Kółko wyboru: zaznaczone wypełnia się bursztynem od ramki. */
        default:
          'box-border size-4 shrink-0 rounded-full border-2 border-neutral-300 bg-basic-white data-[state=checked]:border-[5px] data-[state=checked]:border-accent-600',
        /** Cała karta jest polem wyboru — treść i kółko układa wywołanie. */
        card: 'cursor-pointer',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, variant, children, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ variant }), className)}
      {...props}
    >
      {children}
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

/**
 * Karta wyboru: całe pole jest polem wyboru, a układ treści zostaje po stronie
 * wywołania — karty w różnych miejscach różnią się nim całkowicie, mechanika
 * (roving tabindex, strzałki, Spacja) przychodzi z Radiksa.
 *
 * `group` daje wnętrzu dostęp do `data-state` karty, więc kropka i tło reagują
 * na zaznaczenie samym CSS-em.
 */
const RadioCard = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  Omit<RadioGroupItemProps, 'variant'>
>(({ className, ...props }, ref) => (
  <RadioGroupItem
    ref={ref}
    variant="card"
    className={cn(
      'group rounded-[11px] text-left transition-colors',
      className,
    )}
    {...props}
  />
));
RadioCard.displayName = 'RadioCard';

/**
 * Kółko wyboru wewnątrz `RadioCard`. Stan bierze z `data-state` karty, nie
 * z propsa — inaczej wywołanie musiałoby liczyć zaznaczenie drugi raz, obok
 * Radiksa, i mogłoby się z nim rozjechać.
 */
const RadioDot = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    aria-hidden
    className={cn(
      'box-border size-4 shrink-0 rounded-full border-2 border-neutral-300 bg-basic-white',
      'group-data-[state=checked]:border-[5px] group-data-[state=checked]:border-accent-600',
      className,
    )}
    {...props}
  />
);
RadioDot.displayName = 'RadioDot';

export { RadioGroup, RadioGroupItem, RadioCard, RadioDot };
